import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/Navbar';
import Footer from '../../../home/components/Footer';

export default function SecurityLightingPage() {
  useEffect(() => {
    document.title = 'Security Lighting Watford | Beyond Electrical';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Professional security lighting installation in Watford. Motion sensor and PIR lighting by NICEIC electricians. Call 020 3198 1818 for a free quote.');
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beyond Electrical Services",
      "image": "https://beyondelectrical.co.uk/logo.png",
      "telephone": "020 3198 1818",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Watford",
        "addressLocality": "Watford",
        "addressRegion": "Hertfordshire",
        "postalCode": "WD17",
        "addressCountry": "GB"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 51.6565,
        "longitude": -0.3957
      },
      "url": "https://beyondelectrical.co.uk/services/installations/security-lighting",
      "areaServed": "Watford",
      "serviceType": "Security Lighting",
      "openingHours": "Mo-Su 00:00-23:59"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]"></div>
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/2f4225656525a7172b062f28a32e2043.jpg')] bg-cover bg-center opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-shield-check-fill"></i>
              NICEIC Certified
            </div>
            <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
              <i className="ri-map-pin-fill"></i>
              Watford Security Experts
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Security Lighting<br />
            <span className="text-[#FFD700]">in Watford</span>
          </h1>

          <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
            Protect your Watford property with professional security lighting installation. Motion sensor lights, floodlights, and dusk-to-dawn lighting to deter intruders and enhance safety.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-xl shadow-[#FFD700]/30 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill"></i>
              020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill"></i>
              Free Quote
            </a>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our Security Lighting Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive security lighting solutions for Watford properties
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🔦
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Motion Sensor Lights</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    PIR motion sensor security lights that automatically activate when movement is detected around your Watford property.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  💡
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">LED Floodlights</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Powerful LED floodlights for driveways, gardens, and building perimeters throughout Watford, providing bright security illumination.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🌙
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Dusk-to-Dawn Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Automatic security lights with photocell sensors that turn on at dusk and off at dawn for continuous protection in Watford.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🏠
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Perimeter Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Strategic lighting around property boundaries, fences, and gates to eliminate dark spots in your Watford property.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🚪
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Door &amp; Entry Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Bright security lighting for front doors, back doors, and side entrances to deter intruders at access points in Watford homes.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 hover:shadow-xl transition-all duration-300">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FFD700] rounded-xl text-2xl flex-shrink-0">
                  🤖
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#0A1628] mb-2">Smart Security Lighting</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    App-controlled security lights with scheduling, remote activation, and integration with smart home systems for Watford properties.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Benefits of Security Lighting in Watford
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Why security lighting is essential for your property
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🛡️
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Deter Intruders</h3>
              <p className="text-gray-600 leading-relaxed">
                Well-lit properties are proven to deter burglars and intruders, keeping your Watford home and family safe.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                👁️
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Improved Visibility</h3>
              <p className="text-gray-600 leading-relaxed">
                See clearly around your Watford property at night, identifying visitors and monitoring outdoor areas effectively.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🚶
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Prevent Accidents</h3>
              <p className="text-gray-600 leading-relaxed">
                Reduce trips, falls, and accidents with well-lit pathways, steps, and outdoor areas around your Watford home.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                💰
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Lower Insurance</h3>
              <p className="text-gray-600 leading-relaxed">
                Some insurance companies offer discounts for properties with security lighting installed in Watford.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                🌙
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">24/7 Protection</h3>
              <p className="text-gray-600 leading-relaxed">
                Automatic security lighting provides round-the-clock protection without you having to remember to switch lights on in Watford.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl text-4xl mx-auto mb-4">
                ⚡
              </div>
              <h3 className="text-xl font-bold text-[#0A1628] mb-3">Energy Efficient</h3>
              <p className="text-gray-600 leading-relaxed">
                Modern LED security lights use minimal electricity while providing powerful illumination for Watford properties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Security Lighting Features
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Advanced features for maximum protection in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">⏱️</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Adjustable Timers</h3>
              <p className="text-gray-600 text-sm">
                Set how long lights stay on after detection
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🎯</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Detection Range</h3>
              <p className="text-gray-600 text-sm">
                Adjustable sensor range up to 12 meters
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💡</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Brightness Control</h3>
              <p className="text-gray-600 text-sm">
                Adjustable light sensitivity settings
              </p>
            </div>

            <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💧</div>
              <h3 className="text-lg font-bold text-[#0A1628] mb-2">Weatherproof</h3>
              <p className="text-gray-600 text-sm">
                IP65+ rated for all weather conditions
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] to-[#0F2847]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Trusted security lighting specialists in Watford
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🏆</div>
              <h3 className="text-xl font-bold text-white mb-2">15 Years Experience</h3>
              <p className="text-gray-300 text-sm">
                Security lighting experts
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">✅</div>
              <h3 className="text-xl font-bold text-white mb-2">NICEIC Certified</h3>
              <p className="text-gray-300 text-sm">
                Fully qualified electricians
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">🎯</div>
              <h3 className="text-xl font-bold text-white mb-2">Strategic Placement</h3>
              <p className="text-gray-300 text-sm">
                Expert positioning advice
              </p>
            </div>

            <div className="bg-white/5 backdrop-blur-sm border border-[#FFD700]/30 rounded-2xl p-6 text-center">
              <div className="text-4xl mb-3">💯</div>
              <h3 className="text-xl font-bold text-white mb-2">Quality Products</h3>
              <p className="text-gray-300 text-sm">
                Premium security lights
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-6">
            Security Lighting Across Watford
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional security lighting installation throughout Watford, Rickmansworth, and surrounding areas
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {['Watford', 'Rickmansworth', 'Bushey', 'Croxley Green', 'Abbots Langley', 'Kings Langley', 'Chorleywood', 'Northwood', 'Pinner', 'Harrow', 'Borehamwood', 'Radlett'].map((area) => (
              <span
                key={area}
                className="bg-gradient-to-r from-[#FFD700] to-[#FFC700] text-[#0A1628] px-5 py-2.5 rounded-full font-semibold text-sm shadow-md"
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#0A1628] via-[#0F2847] to-[#0A1628]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Protect Your Watford Property Today
          </h2>
          <p className="text-lg sm:text-xl text-gray-300 mb-10 leading-relaxed">
            Contact us for a free security lighting assessment. We'll survey your Watford property and recommend the best security lighting solution.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:02031981818"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-10 py-5 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-2xl shadow-[#FFD700]/40 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call 020 3198 1818
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-mail-fill text-xl"></i>
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}